import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram } from "lucide-react";
import { IntegradynLogo } from "@/components/integradyn-logo";
import { Link } from "wouter";
import madeInAmericaImg from "../assets/made-in-america.webp";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const aiLinks = [
    { label: "AI Consulting", href: "/ai-agents" },
    { label: "How the Program Works", href: "/ai-agents#how-it-works" },
    { label: "What We Can Build", href: "/ai-agents#departments" },
    { label: "Book a Consultation", href: "/contact" },
    { label: "Blog", href: "/blog" },
  ];

  const webDevLinks = [
    { label: "Web Development", href: "/web-development" },
    { label: "Case Studies", href: "/case-studies/automatic-climate-hvac" },
    { label: "Projects", href: "/projects" },
    { label: "Pricing", href: "/pricing" },
  ];

  const voiceLinks = [
    { label: "Integradyn Voice", href: "/voice" },
    { label: "What It Does", href: "/voice#features" },
    { label: "Train It Yourself", href: "/voice#training" },
    { label: "Book a Demo", href: "/contact" },
  ];

  const aboutLinks = [
    { label: "About", href: "/about" },
    { label: "Reviews", href: "/reviews" },
    { label: "Promotions", href: "/promotions" },
    { label: "Contact Us", href: "/contact" },
  ];

  const legalLinks = [
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms of Service", href: "/terms-of-service" },
    { label: "Results Disclaimer", href: "/disclaimer" },
    { label: "Sitemap", href: "/sitemap" },
    { label: "Accessibility", href: "/accessibility" },
    { label: "Admin Portal", href: "/admin" },
  ];

  return (
    <footer className="bg-[#0a0a0a] text-white pt-20 pb-8 relative overflow-hidden">
      <div className="absolute inset-0 dot-pattern opacity-30 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/50 to-[#0a0a0a] pointer-events-none" />

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Brand + nav grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-10 mb-14">

          {/* Brand column — spans 2 on lg */}
          <div className="lg:col-span-2">
            <div className="mb-5" data-testid="footer-logo">
              <IntegradynLogo variant="full" size="lg" inverted={false} />
            </div>
            <p className="text-gray-400 mb-6 max-w-md text-sm leading-relaxed">
              We build three things for service businesses: custom websites with a built-in dashboard, Integradyn Voice — an AI receptionist that answers and books jobs 24/7 — and an AI consulting program that plans, builds, and runs automation with you, on your own hardware when you want it.
            </p>

            {/* Social icons */}
            <div className="flex items-center gap-3 mb-7">
              {[
                { Icon: Facebook, label: "Facebook", color: "border-[#00d4ff] hover:bg-[#00d4ff]" },
                { Icon: Twitter, label: "Twitter", color: "border-[#ff1493] hover:bg-[#ff1493]" },
                { Icon: Linkedin, label: "LinkedIn", color: "border-[#39ff14] hover:bg-[#39ff14]" },
                { Icon: Instagram, label: "Instagram", color: "border-[#a855f7] hover:bg-[#a855f7]" },
              ].map(({ Icon, label, color }) => (
                <a
                  key={label}
                  href="#"
                  className={`w-10 h-10 bg-[#1a1a1a] border ${color} rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110`}
                  data-testid={`social-${label.toLowerCase()}`}
                  aria-label={label}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>

            {/* Contact block */}
            <div className="bg-white/5 rounded-xl p-5 border border-white/10 space-y-3">
              <div className="flex items-start gap-3 text-sm text-gray-400">
                <MapPin className="w-4 h-4 text-[#0BC9B2] flex-shrink-0 mt-0.5" />
                <span>Serving businesses nationwide across the USA</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#0BC9B2] flex-shrink-0" />
                <a href="tel:+18667561020" className="text-base font-bold bg-gradient-to-r from-[#0BC9B2] to-[#FF8A3D] bg-clip-text text-transparent" data-testid="footer-phone">
                  (866) 756-1020
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <Mail className="w-4 h-4 text-[#0BC9B2] flex-shrink-0" />
                <a href="mailto:sales@integradyn.ai" className="hover:text-[#0BC9B2] transition-colors">
                  sales@integradyn.ai
                </a>
              </div>
            </div>
          </div>

          {/* AI Consulting column */}
          <div>
            <h3 className="text-xs font-bold mb-5 text-white uppercase tracking-wider">AI Consulting</h3>
            <ul className="space-y-3">
              {aiLinks.map((link) => {
                const inner = (
                  <span
                    className="text-gray-400 hover:text-[#0BC9B2] transition-colors inline-flex items-center group cursor-pointer text-sm"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span className="w-0 h-0.5 bg-[#0BC9B2] group-hover:w-3 transition-all duration-300 mr-0 group-hover:mr-2 flex-shrink-0" />
                    {link.label}
                  </span>
                );
                return (
                  <li key={link.href}>
                    {link.href.includes("#") ? (
                      <a href={link.href} className="no-underline">{inner}</a>
                    ) : (
                      <Link href={link.href}>{inner}</Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Web Development column */}
          <div>
            <h3 className="text-xs font-bold mb-5 text-white uppercase tracking-wider">Web Development</h3>
            <ul className="space-y-3">
              {webDevLinks.map((link) => (
                <li key={link.href}>
                  <Link href={link.href}>
                    <span
                      className="text-gray-400 hover:text-[#FF8A3D] transition-colors inline-flex items-center group cursor-pointer text-sm"
                      data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <span className="w-0 h-0.5 bg-[#FF8A3D] group-hover:w-3 transition-all duration-300 mr-0 group-hover:mr-2 flex-shrink-0" />
                      {link.label}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* About column */}
          <div>
            <h3 className="text-xs font-bold mb-5 text-white uppercase tracking-wider">About</h3>
            <ul className="space-y-3">
              {aboutLinks.map((link) => (
                <li key={link.href}>
                  <Link href={link.href}>
                    <span
                      className="text-gray-400 hover:text-white transition-colors inline-flex items-center group cursor-pointer text-sm"
                      data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <span className="w-0 h-0.5 bg-white group-hover:w-3 transition-all duration-300 mr-0 group-hover:mr-2 flex-shrink-0" />
                      {link.label}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Integradyn Voice column */}
          <div>
            <h3 className="text-xs font-bold mb-5 text-white uppercase tracking-wider">AI Receptionist</h3>
            <ul className="space-y-3">
              {voiceLinks.map((link) => {
                const inner = (
                  <span
                    className="text-gray-400 hover:text-[#a78bfa] transition-colors inline-flex items-center group cursor-pointer text-sm"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span className="w-0 h-0.5 bg-[#a78bfa] group-hover:w-3 transition-all duration-300 mr-0 group-hover:mr-2 flex-shrink-0" />
                    {link.label}
                  </span>
                );
                return (
                  <li key={link.href}>
                    {link.href.includes("#") ? (
                      <a href={link.href} className="no-underline">{inner}</a>
                    ) : (
                      <Link href={link.href}>{inner}</Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Legal column */}
          <div>
            <h3 className="text-xs font-bold mb-5 text-white uppercase tracking-wider">Legal</h3>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <Link href={link.href}>
                    <span
                      className={`text-gray-400 transition-colors inline-flex items-center group cursor-pointer text-sm ${link.label === "Admin Portal" ? "hover:text-[#a855f7]" : "hover:text-white"}`}
                      data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <span className={`w-0 h-0.5 ${link.label === "Admin Portal" ? "bg-[#a855f7]" : "bg-white"} group-hover:w-3 transition-all duration-300 mr-0 group-hover:mr-2 flex-shrink-0`} />
                      {link.label}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Results disclaimer */}
        <div className="border-t border-gray-800 pt-8 mb-8">
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <p className="text-gray-400 text-xs leading-relaxed">
              <span className="font-semibold text-[#ff1493]">Results Disclaimer:</span> The results, testimonials, and case studies shown on this website are not typical and are not guaranteed. Individual results vary based on factors including your industry, competition, business practices, and ability to close leads. Our services provide targeted website traffic; we do not guarantee leads, sales, or revenue outcomes. AI features are provided "as is" and may require human review; no warranties on performance.{" "}
              <Link href="/disclaimer">
                <span className="font-bold underline text-lime-500 hover:text-lime-300 transition-colors cursor-pointer" data-testid="link-footer-disclaimer">
                  Read our full Results Disclaimer
                </span>
              </Link>
            </p>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <img
                src={madeInAmericaImg}
                alt="Made in America"
                width={60}
                height={39}
                className="w-15 h-auto object-contain rounded opacity-80"
                loading="lazy"
                decoding="async"
              />
              <p className="text-gray-500 text-sm">&copy; {currentYear} Eric Miller Inc. DBA Integradyn. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
